import { cn } from '@today/ui-primitives'
import type { ReactElement } from 'react'

/** Severity of one notification; drives its styling and whether it is an alert. */
export enum NotificationLevel {
	INFO = 'INFO',
	SUCCESS = 'SUCCESS',
	WARNING = 'WARNING',
	ERROR = 'ERROR',
}

/**
 * An optional action on a notification (e.g. Undo).
 *
 * @property label - Button text and accessible name (product copy, supplied by the consumer).
 * @property onAction - Called when the button is pressed.
 */
export interface NotificationAction {
	label: string
	onAction: () => void
}

/**
 * One notification row.
 *
 * @property id - Stable identity, passed back to `onDismiss`.
 * @property level - Severity.
 * @property title - Short headline (product copy, supplied by the consumer).
 * @property description - Optional detail line.
 * @property action - Optional action button shown before the dismiss button.
 */
export interface NotificationStatusBarItem {
	id: string
	level: NotificationLevel
	title: string
	description?: string
	action?: NotificationAction
}

/**
 * Built-in strings of {@link NotificationStatusBar}, overridable per locale (tech §59).
 *
 * @property region - Accessible name of the status region. Default `Notifications`.
 * @property dismiss - Accessible name of each dismiss button. Default `Dismiss`.
 */
export interface NotificationStatusBarLabels {
	region: string
	dismiss: string
}

const DEFAULT_LABELS: NotificationStatusBarLabels = { region: 'Notifications', dismiss: 'Dismiss' }

/**
 * Props for the {@link NotificationStatusBar} molecule.
 *
 * @property items - Notifications in display order (the consumer orders them).
 * @property onDismiss - Called with an item's id when its dismiss button is pressed.
 * @property labels - Localised built-in strings; each omitted key falls back to English.
 */
export interface NotificationStatusBarProps {
	items: NotificationStatusBarItem[]
	onDismiss: (id: string) => void
	labels?: Partial<NotificationStatusBarLabels>
}

const LEVEL_CLASSES: Record<NotificationLevel, string> = {
	[NotificationLevel.INFO]: 'border-ds-info',
	[NotificationLevel.SUCCESS]: 'border-ds-success',
	[NotificationLevel.WARNING]: 'border-ds-warning',
	[NotificationLevel.ERROR]: 'border-ds-danger',
}

/**
 * A stack of transient notifications with a dismiss control each.
 *
 * @remarks
 * - **Accessibility:** one polite `role="status"` region named `labels.region`
 *   announces new items; an ERROR item's content is additionally `role="alert"` so it is
 *   announced assertively. Each dismiss button is named `labels.dismiss`.
 * - **Action:** an item's optional `action` renders a button named by its label,
 *   before the dismiss button; pressing it calls `action.onAction` then `onDismiss`.
 * - **Presentational only:** no timers, no store — the consumer owns the list.
 *
 * @see packages/ui-components/src/notification-status-bar/notification-status-bar.stories.tsx
 */
export function NotificationStatusBar({ items, onDismiss, labels }: NotificationStatusBarProps): ReactElement {
	const text = { ...DEFAULT_LABELS, ...labels }
	return (
		<div role="status" aria-live="polite" aria-label={text.region} className="flex flex-col gap-ds-2">
			<ul className="flex flex-col gap-ds-2">
				{items.map((item) => (
					<li
						key={item.id}
						className={cn(
							'flex items-start gap-ds-2 rounded-ds-md border bg-ds-surface-elevated p-3 text-ds-sm text-ds-text',
							LEVEL_CLASSES[item.level],
						)}
					>
						<div role={item.level === NotificationLevel.ERROR ? 'alert' : undefined} className="flex-1 min-w-0">
							<strong className="block font-medium">{item.title}</strong>
							{item.description && <span className="block">{item.description}</span>}
						</div>
						{item.action && (
							<button
								type="button"
								onClick={() => {
									item.action?.onAction()
									onDismiss(item.id)
								}}
								className="shrink-0 rounded-ds-sm px-1 font-medium text-ds-text underline focus:outline-none focus-visible:ring-2 focus-visible:ring-ds-ring"
							>
								{item.action.label}
							</button>
						)}
						<button
							type="button"
							aria-label={text.dismiss}
							onClick={() => onDismiss(item.id)}
							className="shrink-0 rounded-ds-sm px-1 text-ds-text-muted focus:outline-none focus-visible:ring-2 focus-visible:ring-ds-ring"
						>
							<span aria-hidden="true">×</span>
						</button>
					</li>
				))}
			</ul>
		</div>
	)
}
